import { useCallback, useContext, useMemo } from "react";
import {
    OutstandingCashItem,
    TransactionForReconciliationFromStatement,
} from "@common/contracts/reconciliation.contract";
import {
    getStatmentTransactionId,
    isDateOnlyOutsideStatementPeriod,
} from "@common/helpers/reconciliation";
import {
    canCashItemClearOnDate,
    getTransactionDatedBeforeCashItemMessage,
} from "@common/helpers/reconciliationCashItems";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { FinancialAccount } from "@common/types/financialAccount";
import { isLockedByDateOnly } from "@helpers/lock";
import { useEntitiesById } from "@hooks/useEntities";
import { useUncategorizedCategory } from "@hooks/useUncategorizedCategory";
import {
    getCheckMergeTargetKey,
    ReconciliationContext,
} from "./Reconciliation.context";
import { buildStatementLineRows } from "./statementLineRows";

interface UseStatementLineRowsParams {
    checkToMatch: OutstandingCashItem | undefined;
    statementLines: TransactionForReconciliationFromStatement[];
    financialAccount: FinancialAccount;
    lockDate: Date | null;
    openingDate: string;
    closingDate: string;
}

export function useStatementLineRows({
    checkToMatch,
    statementLines,
    financialAccount,
    lockDate,
    openingDate,
    closingDate,
}: UseStatementLineRowsParams) {
    const { checkMerges } = useContext(ReconciliationContext);
    const entitiesById = useEntitiesById();
    const entity =
        financialAccount.entityId === null
            ? undefined
            : entitiesById.get(financialAccount.entityId);
    const uncategorizedCategory = useUncategorizedCategory();

    const statementLineRows = useMemo(
        () =>
            checkToMatch && entity && uncategorizedCategory
                ? buildStatementLineRows({
                      checkToMatch,
                      statementLines,
                      financialAccount,
                      entity,
                      categoryId: uncategorizedCategory.id,
                  })
                : [],
        [
            checkToMatch,
            statementLines,
            financialAccount,
            entity,
            uncategorizedCategory,
        ],
    );
    const rows = useMemo(
        () => statementLineRows.map(({ row }) => row),
        [statementLineRows],
    );
    const lineByRowId = useMemo(
        () => new Map(statementLineRows.map(({ row, line }) => [row.id, line])),
        [statementLineRows],
    );

    const findStatementLine = useCallback(
        (transaction: TransactionListItem) => lineByRowId.get(transaction.id),
        [lineByRowId],
    );

    const getStatementLineDisabledReason = useCallback(
        (line: TransactionForReconciliationFromStatement) => {
            if (
                checkToMatch &&
                !canCashItemClearOnDate({
                    cashItemDateOnly: checkToMatch.date,
                    clearedDateOnly: line.date,
                })
            ) {
                return getTransactionDatedBeforeCashItemMessage(
                    checkToMatch.type,
                );
            }
            if (
                isDateOnlyOutsideStatementPeriod({
                    dateOnly: line.date,
                    openingDate,
                    closingDate,
                })
            ) {
                return "Transaction is outside the statement period";
            }
            if (isLockedByDateOnly(lockDate, line.date)) {
                return "Transaction is locked";
            }
            const targetKey = getCheckMergeTargetKey({
                statementTransactionId: getStatmentTransactionId(line),
            });
            const isUsedByAnotherCheck = checkMerges.some(
                (merge) =>
                    getCheckMergeTargetKey(merge) === targetKey &&
                    merge.checkTransactionId !==
                        checkToMatch?.financialTransactionId,
            );
            if (isUsedByAnotherCheck) {
                return "Transaction is already clearing another check or deposit";
            }
            return undefined;
        },
        [openingDate, closingDate, lockDate, checkMerges, checkToMatch],
    );

    return {
        rows,
        findStatementLine,
        getStatementLineDisabledReason,
    };
}
